import React, { createContext, useContext, useEffect, useRef, useCallback, useState } from 'react';
import apiService from '../services/apiService';

const SyncContext = createContext(null);

export const useSync = () => {
  const ctx = useContext(SyncContext);
  if (!ctx) return {}; // Retornar vacío si no hay provider (antes del login)
  return ctx;
};

const isWebMode = !(window.electron && window.electron.ipcRenderer);

export const SyncProvider = ({ isAuthenticated, children }) => {
  const [active, setActive] = useState(false);
  const [interval, setIntervalMin] = useState(5);
  const [countdown, setCountdown] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [lastResult, setLastResult] = useState(null);
  const [syncHistory, setSyncHistory] = useState([]); // últimos N resultados
  const [showModal, setShowModal] = useState(false);
  const [syncConfig, setSyncConfig] = useState(null); // {url, negocio, sucursal}

  const timerRef = useRef(null);
  const countdownRef = useRef(null);
  const syncingRef = useRef(false);
  const configLoadedRef = useRef(false);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (countdownRef.current) { clearInterval(countdownRef.current); countdownRef.current = null; }
  }, []);

  const addToHistory = useCallback((result) => {
    const entry = {
      ...result,
      timestamp: new Date().toISOString()
    };
    setSyncHistory(prev => [entry, ...prev].slice(0, 20));
  }, []);

  const runSync = useCallback(async () => {
    if (syncingRef.current) return null;
    syncingRef.current = true;
    setSyncing(true);
    let result = null;
    try {
      console.log('[AutoSync] Ejecutando sync...');
      result = await apiService.syncFullSync();
      if (result.success) {
        const pushEnv = result.data?.push?.resumen?.total_enviados || 0;
        const pullRec = result.data?.pull?.resumen?.total_recibidos || 0;
        console.log(`[AutoSync] OK: ${pushEnv} enviados, ${pullRec} recibidos`);
      } else {
        console.error('[AutoSync] Error:', result.message);
      }
      setLastResult(result);
      addToHistory(result);
    } catch (e) {
      console.error('[AutoSync] Error:', e.message);
      result = { success: false, message: e.message };
      setLastResult(result);
      addToHistory(result);
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
    return result;
  }, [addToHistory]);

  const startTimer = useCallback((intervalMinParam) => {
    clearTimers();
    const mins = intervalMinParam || interval;
    const intervalMs = mins * 60 * 1000;

    setCountdown(mins * 60);

    countdownRef.current = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) return mins * 60;
        return prev - 1;
      });
    }, 1000);

    timerRef.current = setInterval(() => {
      runSync().then(() => {
        setCountdown(mins * 60);
      });
    }, intervalMs);
  }, [clearTimers, runSync, interval]);

  // Activar auto-sync (llamado desde Settings)
  const activate = useCallback(async (mins) => {
    const m = mins || interval;
    setIntervalMin(m);
    setActive(true);
    startTimer(m);
    // Guardar en BD
    try {
      await apiService.actualizarConfiguraciones([
        { clave: 'caja_api_nube_auto_sync', valor: '1' },
        { clave: 'caja_api_nube_auto_sync_interval', valor: String(m) }
      ], null);
    } catch (e) {
      console.error('Error guardando config auto-sync:', e);
    }
  }, [interval, startTimer]);

  // Desactivar auto-sync
  const deactivate = useCallback(async () => {
    setActive(false);
    setCountdown(0);
    clearTimers();
    try {
      await apiService.actualizarConfiguraciones([
        { clave: 'caja_api_nube_auto_sync', valor: '0' }
      ], null);
    } catch (e) {
      console.error('Error guardando config auto-sync:', e);
    }
  }, [clearTimers]);

  // Sync manual (push, pull, full)
  const manualSync = useCallback(async (type = 'full') => {
    if (syncingRef.current) return null;
    syncingRef.current = true;
    setSyncing(true);
    let result = null;
    try {
      if (type === 'push') result = await apiService.syncPush();
      else if (type === 'pull') result = await apiService.syncPull();
      else result = await apiService.syncFullSync();
      setLastResult(result);
      addToHistory(result);
    } catch (e) {
      result = { success: false, message: e.message };
      setLastResult(result);
      addToHistory(result);
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
    return result;
  }, [addToHistory]);

  // Cargar config al autenticarse
  useEffect(() => {
    if (!isAuthenticated || isWebMode) {
      clearTimers();
      setActive(false);
      setCountdown(0);
      setSyncConfig(null);
      configLoadedRef.current = false;
      setSyncHistory([]);
      setLastResult(null);
      return;
    }

    if (configLoadedRef.current) return;

    const loadSyncConfig = async () => {
      try {
        const result = await apiService.getConfiguracionesPorCategoria('sync', null);
        if (result.success && result.data?.valores) {
          const vals = result.data.valores;
          const enabled = vals.caja_api_nube_auto_sync === '1';
          const intv = parseInt(vals.caja_api_nube_auto_sync_interval || '5', 10);
          const url = (vals.caja_api_nube_url || '').trim();
          const negocio = (vals.caja_api_nube_negocio || '').trim();
          const sucursal = (vals.caja_api_nube_sucursal || '').trim();

          setIntervalMin(intv);
          setSyncConfig(url && negocio && sucursal ? { url, negocio, sucursal } : null);

          if (enabled && url && negocio && sucursal) {
            console.log(`[AutoSync] Iniciando automáticamente (cada ${intv} min)`);
            setActive(true);
            startTimer(intv);
          }
        }
        configLoadedRef.current = true;
      } catch (e) {
        console.error('[AutoSync] Error cargando config:', e);
      }
    };

    const timeout = setTimeout(loadSyncConfig, 1500);
    return () => clearTimeout(timeout);
  }, [isAuthenticated, clearTimers, startTimer]);

  useEffect(() => {
    return () => clearTimers();
  }, [clearTimers]);

  const value = {
    active,
    interval,
    countdown,
    syncing,
    lastResult,
    syncHistory,
    syncConfig,
    showModal,
    setShowModal,
    activate,
    deactivate,
    manualSync,
    runSync,
    setIntervalMin
  };

  return (
    <SyncContext.Provider value={value}>
      {children}
    </SyncContext.Provider>
  );
};

export default SyncContext;
